import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button
} from '@mui/material';
import {
  Science as ScienceIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

const LabReportDialog = ({
  open,
  onClose,
  reportData,
  reportLoading,
  selectedPatient,
  // Map of { [categoryName]: [{ name, dataUri }] } — populated for FBC
  // samples that came in with WBC / RBC / PLT distribution graphs from
  // the analyzer. Defaults to {} so callers that don't pass it (older
  // code paths) just see the existing table-only layout.
  categoryHistograms = {},
  // Map of { [categoryName]: [{ name, text }] } — analyzer-emitted
  // interpretive narratives ("WBC Message: Leucocytosis", etc.). Same
  // shape and key as histograms.
  categoryNarratives = {},
}) => {
 
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            background: 'linear-gradient(135deg, #59c230ff 0%, #5de65dff 100%)',
            color: 'white',
            margin: -3,
            marginBottom: 2,
            borderRadius: '12px 12px 0 0'
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              Laboratory Report
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {selectedPatient?.fullName} • ID: {selectedPatient?.id}
            </Typography>
          </Box>
          {reportLoading && (
            <CircularProgress 
              size={32} 
              sx={{ color: 'white' }} 
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ bgcolor: '#f8f9fd', p: 3 }}>
        {Object.keys(reportData).length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #59c230ff 0%, #539453ff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.2
              }}
            >
              <ScienceIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              No Lab Results Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No laboratory test results found for this patient
            </Typography>
          </Box>
        ) : (
          <Box>
            {Object.keys(reportData)
              .sort((a, b) => new Date(b) - new Date(a))
              .map((date, dateIndex) => (
                <Paper
                  key={date}
                  elevation={0}
                  sx={{ 
                    mb: 3,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid #e8eaf6',
                    bgcolor: 'background.paper'
                  }}
                >
                  {/* Date Header */}
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #1d9b51ff 0%, #1a6e3aff 100%)',
                      p: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      <CalendarIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: 'white',
                          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        {(() => {
                          const [year, month, day] = date.split('-').map(Number);
                          const localDate = new Date(year, month - 1, day);
                          return localDate.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          });
                        })()}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        {Object.keys(reportData[date]).length} test category(ies)
                      </Typography>
                    </Box>
                  </Box>

                  {/* Test Categories */}
                  <Box sx={{ p: 3 }}>
                    {Object.keys(reportData[date])
                      .sort()
                      .map((testCategory, catIndex) => (
                        <Box 
                          key={testCategory} 
                          sx={{ 
                            mb: catIndex < Object.keys(reportData[date]).length - 1 ? 4 : 0 
                          }}
                        >
                          {/* Category Header */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              mb: 2,
                              pb: 1.5,
                              borderBottom: '2px solid #e8eaf6'
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <ScienceIcon sx={{ color: 'white', fontSize: 20 }} />
                            </Box>
                            <Box>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 700,
                                  color: '#2d3561',
                                  letterSpacing: 0.5
                                }}
                              >
                                {testCategory}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {reportData[date][testCategory].length} parameter(s)
                              </Typography>
                            </Box>
                          </Box>

                          {/* Results Table */}
                          <TableContainer 
                            component={Paper} 
                            elevation={0}
                            sx={{ 
                              border: '1px solid #e8eaf6',
                              borderRadius: 2,
                              overflow: 'hidden'
                            }}
                          >
                            <Table size="small">
                              <TableHead>
                                <TableRow 
                                  sx={{ 
                                    background: 'linear-gradient(135deg, #f8f9fd 0%, #f0f2f8 100%)'
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 700, color: '#2d3561' }}>
                                    Parameter
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#2d3561' }}>
                                    Result
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#2d3561' }}>
                                    Unit
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#2d3561' }}>
                                    Reference Range
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#2d3561' }}>
                                    Status
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#2d3561' }}>
                                    Entered By
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {reportData[date][testCategory].map((result, idx) => (
                                  <TableRow 
                                    key={idx} 
                                    hover
                                    sx={{
                                      '&:hover': {
                                        bgcolor: '#f8f9fd'
                                      },
                                      '&:last-child td': {
                                        borderBottom: 0
                                      }
                                    }}
                                  >
                                    <TableCell sx={{ fontWeight: 600 }}>
                                      {result.parameter}
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        sx={{
                                          color: result.flag === 'normal' || result.flag === '-'
                                            ? '#2e7d32'
                                            : result.flag === 'high'
                                            ? '#d32f2f'
                                            : result.flag === 'low'
                                            ? '#ed6c02'
                                            : 'text.primary'
                                        }}
                                      >
                                        {result.result}
                                      </Typography>
                                      {result.result_alt && result.unit_alt && (
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 500 }}>
                                          {result.result_alt} {result.unit_alt}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>
                                      {result.unit}
                                      {result.unit_alt && (
                                        <Typography component="span" sx={{ display: 'block', fontSize: '0.72rem', color: 'text.disabled' }}>
                                          / {result.unit_alt}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                      {result.range}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={result.flag}
                                        size="small"
                                        sx={{
                                          fontWeight: 600,
                                          bgcolor: result.flag === 'normal' || result.flag === '-'
                                            ? 'rgba(46, 125, 50, 0.1)'
                                            : result.flag === 'high'
                                            ? 'rgba(211, 47, 47, 0.1)'
                                            : result.flag === 'low'
                                            ? 'rgba(237, 108, 2, 0.1)'
                                            : 'rgba(0, 0, 0, 0.08)',
                                          color: result.flag === 'normal' || result.flag === '-'
                                            ? '#2e7d32'
                                            : result.flag === 'high'
                                            ? '#d32f2f'
                                            : result.flag === 'low'
                                            ? '#ed6c02'
                                            : 'text.primary',
                                          border: 'none'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                      {result.entered_by}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Analyzer-emitted interpretive narratives
                              ("WBC Message: Anemia, Leucocytosis"). The
                              print templates render them as a beige
                              notes block; mirror that on-screen so the
                              tech can confirm them before printing. */}
                          {Array.isArray(categoryNarratives[testCategory]) &&
                           categoryNarratives[testCategory].length > 0 && (
                            <Box
                              sx={{
                                mt: 2,
                                p: 1.25,
                                border: '1px solid #f1d59c',
                                borderLeft: '4px solid #d4a017',
                                borderRadius: 1.5,
                                bgcolor: '#fdf8ee',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 800,
                                  letterSpacing: 0.6,
                                  textTransform: 'uppercase',
                                  color: '#7a5a10',
                                  display: 'block',
                                  mb: 0.5,
                                }}
                              >
                                Analyzer Notes
                              </Typography>
                              {categoryNarratives[testCategory].map((n, ni) => (
                                <Typography
                                  key={ni}
                                  variant="body2"
                                  sx={{ color: '#2c3e50', lineHeight: 1.5 }}
                                >
                                  <Box component="span" sx={{ fontWeight: 700, color: '#7a5a10', mr: 0.5 }}>
                                    {n.name || 'Message'}:
                                  </Box>
                                  {n.text}
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {/* Distribution graphs from the analyzer — only
                              render when the FBC (or other) sample came
                              with WBC/RBC/PLT histograms. The print
                              template already shows them; this mirrors
                              it on-screen so the tech can confirm the
                              graphs before printing. */}
                          {Array.isArray(categoryHistograms[testCategory]) &&
                           categoryHistograms[testCategory].length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  letterSpacing: 0.6,
                                  textTransform: 'uppercase',
                                  color: '#2d3561',
                                  display: 'block',
                                  mb: 1,
                                }}
                              >
                                Distribution Graphs
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {categoryHistograms[testCategory].map((h, hi) => (
                                  <Box
                                    key={hi}
                                    sx={{
                                      border: '1px solid #e8eaf6',
                                      borderRadius: 1.5,
                                      p: 1,
                                      bgcolor: '#fff',
                                      minWidth: 200,
                                      maxWidth: 260,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        display: 'block',
                                        mb: 0.5,
                                      }}
                                    >
                                      {(h.name || '').replace(/\.\s*(BMP|PNG|JPEG|GIF).*$/i, '').trim() || 'Histogram'}
                                    </Typography>
                                    <img
                                      src={h.dataUri}
                                      alt={h.name || 'Histogram'}
                                      style={{ width: '100%', height: 'auto', display: 'block' }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}
                  </Box>
                </Paper>
              ))}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fd' }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4e803aff 0%, #186b38ff 100%)',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(182, 182, 182, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #663a8e 100%)',
              boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
            }
          }}
        >
          Close Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LabReportDialog;